import React, { memo } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { themeGet } from '@styled-system/theme-get'
import { Box, Flex, IconButton, Link } from 'pcln-design-system'
import { Close as CloseIcon, Menu as MenuIcon, Phone as PhoneIcon } from 'pcln-icons'
import Account from '../Account'
import NavLogo from '../NavLogo'
import useSSO from '../../hooks/useSSO'
import alignmentToJustify from '../../utils/alignmentToJustify'
import { alignmentTypes } from '../../utils/shapes'

const NavContainer = styled(Flex)`
  background-color: ${(props) => props.backgroundColor};
  height: ${(props) => props.height}px;
  position: relative;
`

const LeftLogoFlex = styled(Flex)`
  height: 100%;
`

const RightContent = styled(Flex)`
  height: 100%;
  width: 100%;
`

const RelativeMenuButton = styled(IconButton)`
  &&& {
    display: flex;
    position: relative;
    background-color: transparent;
    color: inherit;
    padding: 0;

    :focus {
      outline: none;
    }

    & svg {
      :focus {
        outline: none;
      }
    }

    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  }
`

const PhoneNumberLink = styled(Link)`
  font-size: ${themeGet('fontSizes.1')}px;
  min-width: fit-content;
  white-space: nowrap;
`

function NavHeader({
  backgroundColor,
  className,
  color,
  hasNavigation,
  height,
  isMenuButtonRightAligned,
  isOpen,
  logoAlignment,
  logoHref,
  logoPath,
  phoneNumber,
  secondaryLogoHref,
  secondaryLogoPadding,
  secondaryLogoPath,
  target,
  zIndex,
  onClick,
}) {
  const { isSignedIn } = useSSO()
  const iconNode = isOpen ? <CloseIcon /> : <MenuIcon />
  const menuLabel = `${isOpen ? 'Close' : 'Open'} navigation menu`
  const justifyContent = secondaryLogoPath ? 'space-between' : alignmentToJustify(logoAlignment)
  const isLogoLeftAligned = logoAlignment === 'left' || justifyContent === 'space-between'
  const isLogoRightAligned = logoAlignment === 'right'
  const isLogoCenterAligned = logoAlignment === 'center' && justifyContent !== 'space-between'

  return (
    <Box className={className}>
      <NavContainer
        alignItems='center'
        backgroundColor={backgroundColor}
        color={color}
        data-testid='nav-header'
        height={height}
        py={2}
        px={3}
      >
        {!isMenuButtonRightAligned && hasNavigation && (
          <RelativeMenuButton
            label={menuLabel}
            icon={iconNode}
            color={color}
            mr={isLogoLeftAligned ? 16 : undefined}
            onClick={onClick}
          />
        )}
        {isLogoLeftAligned && (
          <LeftLogoFlex data-testid='left-flex' justifyContent={justifyContent}>
            <NavLogo href={logoHref} path={logoPath} target={target} zIndex={zIndex} />
          </LeftLogoFlex>
        )}
        <RightContent
          alignItems='center'
          data-testid='right-flex'
          justifyContent={isLogoCenterAligned ? 'center' : 'flex-end'}
          ml={isLogoCenterAligned ? undefined : 2}
        >
          {isLogoCenterAligned && <NavLogo href={logoHref} path={logoPath} target={target} zIndex={zIndex} />}
          {phoneNumber && (
            <Flex alignItems='center' pr={!isSignedIn ? 2 : undefined}>
              <PhoneIcon pr={1} color={color} />
              <PhoneNumberLink href={`tel:${phoneNumber}`} color={color}>
                {phoneNumber}
              </PhoneNumberLink>
            </Flex>
          )}
          {!isSignedIn && <Account isMobile />}
          {isLogoRightAligned && <NavLogo href={logoHref} path={logoPath} target={target} zIndex={zIndex} />}
          {secondaryLogoPath && (
            <NavLogo
              href={secondaryLogoHref}
              leftPadding={secondaryLogoPadding}
              path={secondaryLogoPath}
              target={target}
              zIndex={zIndex}
            />
          )}
          {isMenuButtonRightAligned && hasNavigation && (
            <RelativeMenuButton label={menuLabel} icon={iconNode} onClick={onClick} />
          )}
        </RightContent>
      </NavContainer>
    </Box>
  )
}

NavHeader.defaultProps = {
  logoAlignment: 'left',
}

NavHeader.propTypes = {
  backgroundColor: PropTypes.string,
  className: PropTypes.string,
  color: PropTypes.string,
  hasNavigation: PropTypes.bool,
  height: PropTypes.number,
  isMenuButtonRightAligned: PropTypes.bool,
  isOpen: PropTypes.bool,
  logoAlignment: alignmentTypes,
  logoHref: PropTypes.string,
  logoPath: PropTypes.string,
  phoneNumber: PropTypes.string,
  secondaryLogoHref: PropTypes.string,
  secondaryLogoPadding: PropTypes.number,
  secondaryLogoPath: PropTypes.string,
  target: PropTypes.string,
  zIndex: PropTypes.number,
  onClick: PropTypes.func.isRequired,
}

export default memo(NavHeader)
